/**
 * Logic Flows Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  TableItemsOutput,
  TableDataRow,
  DataSourceConfig,
  TransformerContext
} from '../types';
import { LogicFlow, getCachedLogicFlows } from "@/lib/stores/logicFlowStore";
import { getFolderPath } from "@/lib/stores/folderStore";
import { formatDate, formatTags } from '../shared/formatters';
import { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { formatRelativeTime, convertTagIdsToNames } from "@/lib/utils";
import { getStatusColor } from "@/lib/utils/statusColors";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
    validation: [
      { type: 'required', message: 'Name is required' }
    ]
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  folder: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'folders',
    folderItemType: 'library',
    displayField: 'name',
    valueField: 'id',
  },
  owner: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { 
    key: 'id', 
    label: 'ID', 
    filterable: true, 
    sortable: true,
    metadata: fields.id 
  },
  { 
    key: 'name', 
    label: 'Name', 
    filterable: true, 
    sortable: true,
    editable: 'text',
    metadata: fields.name 
  },
  { 
    key: 'description', 
    label: 'Description', 
    filterable: true,
    editable: 'text',
    metadata: fields.description 
  },
  { 
    key: 'folder', 
    label: 'Folder', 
    filterable: true,
    metadata: fields.folder 
  },
  { 
    key: 'created_at', 
    label: 'Created', 
    align: 'left',
    sortable: true,
    metadata: fields.created_at 
  },
  { 
    key: 'updated_at', 
    label: 'Updated', 
    align: 'left',
    sortable: true,
    metadata: fields.updated_at 
  }
];

// Configuration
const config: DataSourceConfig = {
  type: 'objects',
  title: 'Logic Flows',
  description: 'Visual logic flow diagrams',
  expandable: false,
  icon: 'diagram-project'
};

// Data fetcher
function fetchData(selectedGame?: any, selectedFolderId?: string | null): LogicFlow[] {
  if (!selectedGame?.id) return [];
  
  const allFlows = getCachedLogicFlows(selectedGame.id);
  
  // Filter by folder if specified
  if (selectedFolderId !== undefined && selectedFolderId !== null) {
    return allFlows.filter(flow => (flow as any).folder === selectedFolderId);
  }
  
  return allFlows;
}

// Transformers
function toList(rawData: LogicFlow[]): ListWidgetItem[] {
  return rawData.map(flow => ({
    id: flow.id,
    name: flow.name,
    tags: '',
    type: 'Logic Flow',
    created_at: flow.created_at,
    data: { 
      nodesCount: flow.data.nodes?.length || 0,
      edgesCount: flow.data.edges?.length || 0
    },
    updated_at: flow.updated_at,
    description: flow.description || `${flow.data.nodes?.length || 0} nodes, ${flow.data.edges?.length || 0} connections`
  }));
}

function toTable(rawData: LogicFlow[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(flow => ({
      id: flow.id,
      name: String(flow.name || ''),
      description: String(flow.description || ''),
      folder: getFolderPath(flow.game_id, 'library', (flow as any).folder) || '',
      created_at: formatDate(flow.created_at),
      updated_at: formatDate(flow.updated_at),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

/**
 * Transform raw data to CardData format
 */
function toCard(rawData: LogicFlow[], context?: TransformerContext): CardData[] {
  const { tags = [], getUserNameById } = context || {};
  
  return rawData.map(flow => {
    const nodesCount = flow.data.nodes?.length || 0;
    const edgesCount = flow.data.edges?.length || 0;
    
    const cardData = {
      id: flow.id,
      gameId: flow.game_id,
      title: flow.name || '',
      owner: '',
      creator: "System",
      tags: [],
      description: flow.description || `${nodesCount} nodes, ${edgesCount} connections`,
      lastUpdate: formatRelativeTime(flow.updated_at),
      version: '1.0',
      icon: 'fa-diagram-project',
      iconBg: '#8B5CF6',
      shadowType: "black" as const,
      shadowColor: '#000000',
      statusColor: '#000000',
      hasVersionMismatch: false,
      folder: (flow as any).folder,
      itemType: 'flows',
    };
    return cardData;
  });
}

/**
 * Column keys for flows table view
 */
const TABLE_COLUMNS = [
  "Name", "Description", "Nodes", "Connections", "Last Update", "Folder"
];

/**
 * Transform raw data to TableItem[] format for MainTableWidget
 */
function toTableItems(rawData: LogicFlow[], context?: TransformerContext): TableItemsOutput {
  const items: TableDataRow[] = rawData.map(flow => {
    const nodesCount = flow.data.nodes?.length || 0;
    const edgesCount = flow.data.edges?.length || 0;
    
    return {
      type: "row" as const,
      key: flow.id,
      depth: 0,
      isLocked: false,
      editPermissions: 'Open' as const,
      content: TABLE_COLUMNS.map((columnKey, index) => {
        const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
        let content: string | boolean | number = "";

        switch (normalizedKey) {
          case "name":
            content = flow.name || "";
            break;
          case "description":
            content = flow.description || "";
            break;
          case "nodes":
            content = nodesCount;
            break;
          case "connections":
            content = edgesCount;
            break;
          case "lastupdate":
            content = formatRelativeTime(flow.updated_at);
            break;
          case "folder":
            content = getFolderPath(flow.game_id, 'library', (flow as any).folder) || "";
            break;
          default:
            content = "";
        }

        return {
          key: columnKey,
          fixedX: index === 0,
          content,
        };
      }),
    };
  });

  return {
    items,
    columns: TABLE_COLUMNS,
    fieldMetadata: fields,
  };
}

// Export the module
export const flowsDataSource: DataSourceModule = {
  type: 'objects',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable,
    toCard,
    toTableItems
  }
};

// Export table columns for use in components
export { TABLE_COLUMNS as FLOWS_TABLE_COLUMNS };

